import { Store } from "redux";
import { IStore } from "reducers";
import { INVENTORY, WORKING_ORDER, WORKING_ORDER_STATE } from "constants/action-types";
import _ from "lodash";
import type { IReplenishmentStateAction } from "types/working-order/inventory";
import type { IReplenishment } from "types/inventory";
import type { IAction } from "epics/base-epic";
import me from "models/me";
import { AvailabilityMeta, AvailabilityMetaRequestOptions } from "types/working-order/inventory";
import { forkJoin } from "rxjs/observable/forkJoin";
import { Observable } from "rxjs";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import { add as snackbarAdd } from "actions/snackbars";
import { format } from "lib/globalize-util";
import { stringify } from "querystring";

export const requestWorkingOrderInventory = (action$, store: Store<IStore>) =>
    action$.ofType(WORKING_ORDER.LOAD_INVENTORY).mergeMap((action: IReplenishmentStateAction) => {
        const { lineItems, warehouses, availabilityMap } = action.payload;
        const { cart, working } = store.getState();
        const products = working.products.payload;
        if (_.isEmpty(products)) {
            return [];
        }
        const replenishmentToLoad = {};

        _.each(lineItems, (lineItem) => {
            const product = products?.[lineItem.product];
            if (lineItem.prebook || !product?.__tracks_inventory) {
                return [];
            }
            const warehouseList = _.map(product.warehouses, (warehouse) => warehouse?.ref || warehouse);
            const productWarehouses = me.hasPermission("view_all_warehouses")
                ? warehouseList
                : _.intersection(warehouseList, warehouses);

            const productAvailableWarehouses = availabilityMap // TODO: temp solution, remove after we'll be able to get availabilityMap from delivery windows
                ? _.intersection(availabilityMap[product._id], productWarehouses)
                : productWarehouses;

            const replenishmentWarehouses = cart.isBulkOrder
                ? _.compact(productAvailableWarehouses)
                : [lineItem.warehouse || _.head(productAvailableWarehouses)];

            _.each(replenishmentWarehouses, (warehouse: string) => {
                replenishmentToLoad[warehouse] ||= { warehouse, skusByProductId: {} };
                replenishmentToLoad[warehouse].skusByProductId[product._id] = _.map(product.sizes, "_id");
            });
        });
        if (!_.isEmpty(replenishmentToLoad)) {
            return [
                {
                    type: INVENTORY.REPLENISHMENTS.REQUEST,
                    payload: replenishmentToLoad,
                    meta: action.meta,
                },
            ];
        }
        return [];
    });

export const replaceWorkingOrderInventory = (action$, store: Store<IStore>) =>
    action$.ofType(WORKING_ORDER.REPLACE_INVENTORY).mergeMap((action: IAction<IReplenishment[]>) => {
        store.dispatch({ type: INVENTORY.REPLENISHMENTS.REPLACE, payload: action.payload });
        store.dispatch({
            type: WORKING_ORDER_STATE.UPDATE,
            payload: { replenishments: store.getState().inventory.replenishments },
        });
        return [];
    });

export const fetchProductAvailabilityEpic = (action$) =>
    action$.ofType(WORKING_ORDER.AVAILABILITY.FETCH.START).mergeMap(
        ({ payload, meta }: { payload: AvailabilityMetaRequestOptions; meta: AvailabilityMeta }) =>
            forkJoin(
                Observable.from(_.chunk(payload.ids, 150))
                    .mergeMap(
                        (list) =>
                            ajax({
                                url: `https://${siteConfig.legacyHost || window.location.host.replace("app.", "wholesale.")}/api/v2.0/orders/working/delivery-windows?${stringify({ productId: list.join(",") })}`,
                            }),
                        10,
                    )
                    .toArray(),
            )
                .mergeMap((result) => [
                    {
                        type: WORKING_ORDER.AVAILABILITY.FETCH.SUCCESS,
                        payload: {
                            data: _.reduce(
                                _.flatMap(_.flattenDeep(result), "response"),
                                (acc, entry) => _.mergeWith(acc, entry, (x = {}, y = {}) => ({ ...x, ...y })),
                                {},
                            ),
                            productIds: payload.ids,
                        },
                        meta,
                    },
                ])
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: _.pick(error, ["status", "message"]),
                            meta,
                            type: WORKING_ORDER.AVAILABILITY.FETCH.ERROR,
                        }),
                        Observable.of(snackbarAdd({ message: format("There was an error fetching availability") })),
                    ),
                ),
        1,
    );
